import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getRefCode } from "@/lib/refTracking";
import PricingFAQ from "@/components/PricingFAQ";
import FoundingCounter from "@/components/FoundingCounter";
import Seo from "@/components/Seo";
import {
  Check,
  Truck,
  ShieldCheck,
  Clock,
  Award,
  HeartHandshake,
  Snowflake,
  Fingerprint,
} from "lucide-react";

/* ──────────────────────────────────────────────────────────────────────────
 * The Farm To You — Membership pricing (single plan)
 *
 * Launch pivot: membership-only. ONE plan, two billing intervals
 * ($24.99/mo or $150/yr). Membership is required to access the marketplace
 * once it opens on October 31.
 * ──────────────────────────────────────────────────────────────────────── */

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("yearly");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resendStatus, setResendStatus] = useState("idle");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api
      .get("/memberships/plans")
      .then((r) =>
        setPlans(
          (r.data || []).filter(
            (p) => p.visible !== false && p.tier === "membership"
          )
        )
      )
      .catch(() => {});
  }, []);

  const plan = useMemo(
    () => plans.find((p) => p.interval === interval),
    [plans, interval]
  );
  const monthly = useMemo(() => plans.find((p) => p.interval === "monthly"), [plans]);
  const yearly = useMemo(() => plans.find((p) => p.interval === "yearly"), [plans]);
  const savings =
    monthly && yearly ? Math.round(monthly.price * 12 - yearly.price) : 0;

  const checkout = async () => {
    if (!plan) return;
    if (!user) {
      nav(`/register?next=${encodeURIComponent("/pricing")}`);
      return;
    }
    setLoading(true);
    setErr("");
    setNeedsVerify(false);
    try {
      const refCode = getRefCode();
      const { data } = await api.post("/memberships/checkout", {
        plan_id: plan.id,
        origin_url: window.location.origin,
        ...(refCode ? { ref_code: refCode } : {}),
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const msg = formatApiError(detail) || e.message || "Something went wrong.";
      if (status === 403 && /verify your email/i.test(msg)) {
        setNeedsVerify(true);
        setErr(
          "Please verify your email before subscribing. Check your inbox for the verification link."
        );
      } else {
        setErr(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerify = async () => {
    setResendStatus("sending");
    try {
      await api.post("/auth/resend-verification");
      setResendStatus("sent");
    } catch {
      setResendStatus("error");
    }
  };

  const priceStr = plan ? `$${plan.price.toFixed(2).replace(/\.00$/, "")}` : "—";
  const monthlyEquivalent =
    interval === "yearly" && yearly ? (yearly.price / 12).toFixed(2) : null;

  return (
    <div className="bg-white" data-testid="pricing-page" style={{ backgroundImage: "linear-gradient(rgba(250,247,241,0.9), rgba(250,247,241,0.9)), url('/assets/cattle-field.jpg')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
      <Seo
        title="Become a member · The Farm To You"
        description="One simple membership. 100% American-raised beef & chicken, free shipping, members-only drops. $24.99/mo or $150/yr."
        path="/pricing"
        image="https://customer-assets-lxgj4vgw.emergentagent.net/job_farm-fresh-audit/artifacts/pa0cp03i_tftu.png"
      />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, #FAF7F1 0%, #F3EFE6 55%, #FFFFFF 100%)",
        }}
      >
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(circle, #F4A26155, transparent 65%)" }}
        />
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-12 lg:pt-28 lg:pb-16 relative text-center">
          <p className="overline text-[var(--ff-secondary)]">Membership</p>
          <h1
            className="mt-3 font-display leading-[1.02] text-[var(--ff-text-primary)]"
            style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}
            data-testid="pricing-heading"
          >
            One membership.<br />Everything included.
          </h1>
          <p className="mt-5 text-lg lg:text-xl text-[var(--ff-text-secondary)] max-w-2xl mx-auto leading-relaxed">
            The Farm To You is a members-only marketplace for 100% American-raised
            beef & chicken. One simple plan unlocks every drop, members-only
            pricing, and free shipping — nationwide.
          </p>

          {/* Pillars */}
          <div className="mt-8 flex flex-wrap gap-2 justify-center" data-testid="pricing-pillars">
            {[
              { icon: Truck, label: "Free shipping on every order" },
              { icon: Clock, label: "Members-only limited drops" },
              { icon: ShieldCheck, label: "Pause or cancel anytime" },
            ].map(({ icon: Ic, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/85 backdrop-blur border border-[var(--ff-border)] text-xs text-[var(--ff-text-primary)]"
              >
                <Ic size={12} className="text-[var(--ff-secondary)]" />
                {label}
              </span>
            ))}
          </div>

          {/* Proof icons */}
          <div
            className="mt-6 flex flex-wrap gap-x-6 gap-y-2 items-center justify-center text-[11px] uppercase tracking-wider text-[var(--ff-text-muted)] font-semibold"
            data-testid="pricing-proof-icons"
          >
            {[
              { icon: Award, label: "USDA-inspected", testId: "proof-usda" },
              { icon: HeartHandshake, label: "Family-farmed", testId: "proof-family" },
              { icon: Snowflake, label: "Cold-chain shipping", testId: "proof-coldchain" },
              { icon: Fingerprint, label: "Farm-traceable", testId: "proof-traceable" },
            ].map(({ icon: Ic, label, testId }) => (
              <span key={label} className="inline-flex items-center gap-1.5" data-testid={testId}>
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--ff-primary)] text-white">
                  <Ic size={11} />
                </span>
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Billing toggle */}
      <div className="max-w-5xl mx-auto px-6 mt-12">
        <div className="flex justify-center" data-testid="pricing-toggle">
          <div className="inline-flex items-center bg-[var(--ff-muted)] border border-[var(--ff-border)] rounded-full p-1">
            {[
              { v: "yearly", label: "Annual billing", badge: savings > 0 ? `Save $${savings}` : "Best value" },
              { v: "monthly", label: "Monthly billing", badge: null },
            ].map((b) => (
              <button
                key={b.v}
                onClick={() => setInterval(b.v)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                  interval === b.v
                    ? "bg-[var(--ff-primary)] text-white"
                    : "text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]"
                }`}
                data-testid={`pricing-toggle-${b.v}`}
              >
                {b.label}
                {b.badge && (
                  <span className="ml-2 text-[10px] opacity-90 font-semibold">{b.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {err && (
        <div className="max-w-md mx-auto mt-6 text-center" data-testid="pricing-error">
          <p className="text-rose-700">{err}</p>
          {needsVerify && (
            <div className="mt-3" data-testid="pricing-resend-verify-wrap">
              <button
                type="button"
                onClick={resendVerify}
                disabled={resendStatus === "sending" || resendStatus === "sent"}
                className="text-sm underline text-[var(--ff-primary)] hover:opacity-80 disabled:opacity-60"
                data-testid="pricing-resend-verify-btn"
              >
                {resendStatus === "idle" && "Resend verification email"}
                {resendStatus === "sending" && "Sending…"}
                {resendStatus === "sent" && "Sent — check your inbox"}
                {resendStatus === "error" && "Couldn't resend — try again"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Founding Members — 500-seat scarcity counter */}
      <div className="max-w-xl mx-auto px-6 mt-10" data-testid="pricing-founding-counter">
        <FoundingCounter />
        <p className="mt-3 text-center text-xs text-[var(--ff-text-muted)]">
          The first 500 members become <strong className="text-[var(--ff-primary)]">Founding Members</strong> — lifetime badge + perks. Once 500 seats are claimed, that door closes for good.
        </p>
      </div>

      {/* Single plan card */}
      <section className="max-w-xl mx-auto px-6 pt-10 pb-16" data-testid="pricing-tiers">
        <div
          data-testid="membership-card"
          className="relative rounded-3xl p-8 lg:p-10 bg-[var(--ff-primary)] text-white shadow-[0_25px_60px_-30px_rgba(26,67,49,0.55)]"
        >
          <div
            className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.22em]"
            style={{ background: "#F4A261", color: "var(--ff-primary)" }}
          >
            Members only
          </div>

          <h3 className="font-display text-3xl text-white mt-2">
            The Farm To You Membership
          </h3>
          <p className="text-sm mt-2 text-white/75">
            {interval === "yearly"
              ? "Billed yearly — the best value."
              : "Billed monthly — flexible, cancel anytime."}
          </p>

          <div className="mt-7 flex items-baseline gap-2">
            <span className="font-display text-6xl text-white" data-testid="membership-price">
              {priceStr}
            </span>
            <span className="text-sm text-white/75">
              /{interval === "yearly" ? "year" : "month"}
            </span>
          </div>
          {monthlyEquivalent && (
            <p className="text-xs mt-1 text-white/60">
              ${monthlyEquivalent}/mo equivalent{savings > 0 ? ` · save $${savings} vs monthly` : ""}
            </p>
          )}

          <ul className="mt-7 space-y-3">
            {(plan?.features || []).map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-white/90"
                data-testid={`membership-feat-${i}`}
              >
                <Check size={16} className="shrink-0 mt-0.5 text-[#F4A261]" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={checkout}
            disabled={loading || !plan}
            data-testid="membership-cta"
            className="mt-8 w-full inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-60 bg-[#F4A261] text-[var(--ff-primary)]"
          >
            {loading ? "Redirecting…" : "Become a member"}
          </button>
          <p className="mt-4 text-[11px] text-white/60 text-center">
            Membership is required to shop the marketplace when it opens November 27.
          </p>
        </div>

        {/* Compliance disclaimer */}
        <div
          className="mt-10 rounded-2xl p-6 border border-[var(--ff-border)] bg-[var(--ff-muted)]"
          data-testid="pricing-disclaimer"
        >
          <p className="text-sm text-[var(--ff-text-secondary)] leading-relaxed">
            <strong className="text-[var(--ff-text-primary)]">
              Membership unlocks the whole platform.
            </strong>{" "}
            Shop the full marketplace of 100% American-raised beef &amp; chicken
            anytime, plus limited drops from the local partners we team up with —
            those are available while supplies last and some won&apos;t return
            once they&apos;re gone. Manage everything from your account portal —
            pause, cancel, or update billing whenever you need to.
          </p>
        </div>
      </section>

      {/* PRODUCT OF USA — trust seal */}
      <section
        className="max-w-7xl mx-auto px-6 py-14 flex flex-col items-center text-center"
        data-testid="pricing-product-of-usa"
      >
        <img
          src="https://customer-assets-lxgj4vgw.emergentagent.net/job_farm-fresh-audit/artifacts/m4s2ynsh_Product-of-USA-Label.png"
          alt="Product of USA"
          className="h-28 w-auto"
          loading="lazy"
        />
        <p className="mt-4 font-display text-lg sm:text-xl text-[var(--ff-primary)] max-w-xl">
          Every membership supports American family farms. Raised right here — never imported.
        </p>
      </section>

      {/* FAQ */}
      <PricingFAQ />
    </div>
  );
}
